import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Person,
  Payment,
  Add,
  Receipt,
  Close,
} from '@mui/icons-material';
import { UserDTO, PayoutDTO } from '../types';
import adminService from '../services/adminService';
import CreatePaymentDialog from './CreatePaymentDialog';
import { formatPhone } from '../utils/phoneFormatter';
import { formatAmountFromKopecks /*, kopecksToRubles */ } from '../utils/amountFormatter'; // kopecksToRubles не используется

interface UserDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  user: UserDTO | null;
}

const UserDetailsDialog: React.FC<UserDetailsDialogProps> = ({
  open,
  onClose,
  user,
}) => {
  const [payments, setPayments] = useState<PayoutDTO[]>([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [isCreatePaymentOpen, setIsCreatePaymentOpen] = useState(false);
  // const [isUserDetailsOpen, setIsUserDetailsOpen] = useState(false);
 
  const loadUserPayments = useCallback(async () => {
    if (!user) return;
    
    setIsLoadingPayments(true);
    try {
      const paymentsData = await adminService.getUserPayments(user.id);
      setPayments(paymentsData);
    } catch (error) {
      console.error('Ошибка загрузки выплат:', error);
    } finally {
      setIsLoadingPayments(false);
    }
  }, [user]);

  useEffect(() => {
    if (open && user) {
      loadUserPayments();
    }
  }, [open, user, loadUserPayments]); // Добавляем loadUserPayments в зависимости

  const handleClose = () => {
    setPayments([]);
    setIsLoadingPayments(false);
    onClose();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU');
  };




  if (!user) return null;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Детали пользователя</Typography>
            <IconButton onClick={handleClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Grid container spacing={3}>
            {/* Информация о пользователе */}
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Person sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Информация</Typography>
                </Box>
                
                <Typography variant="body1" gutterBottom>
                  <strong>ФИО:</strong> {user.personalData?.fullName || 'Не указано'}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>ИНН:</strong> {user.personalData?.inn || 'Не указано'}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Телефон:</strong> {formatPhone(String(user.phone))}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Дата регистрации:</strong> {formatDate(user.createdAt)}
                </Typography>
                
                <Box sx={{ mt: 2 }}>
                  <Chip
                    label={user.status === 'Selfemployed' ? 'Активен' : 'Не активен'}
                    color={user.status === 'Selfemployed' ? 'success' : 'error'}
                    sx={{ mr: 1 }}
                  />
                  <Chip
                    label={user.cards && user.cards.length > 0 ? 'Есть карта' : 'Нет карты'}
                    color={user.cards && user.cards.length > 0 ? 'success' : 'warning'}
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Статистика выплат */}
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Payment sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6">Статистика выплат</Typography>
                </Box>
                
                <Typography variant="h4" color="primary" gutterBottom>
                  {formatAmountFromKopecks(payments.filter(p => p.paymentId && p.paymentId > 0 && p.npdBill).reduce((sum, p) => sum + p.amount, 0))}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Общая сумма выплат
                </Typography>
                
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body1" gutterBottom>
                    <strong>Количество выплат:</strong> {payments.length}
                  </Typography>
                  {payments.length > 0 && (
                    <Typography variant="body1">
                      <strong>Последняя выплата:</strong> {formatDate(payments[0].createdAt)}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Список выплат */}
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Payment sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">История выплат</Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setIsCreatePaymentOpen(true)}
                    disabled={!user.cards || user.cards.length === 0}
                  >
                    Создать выплату
                  </Button>
                </Box>

                {(!user.cards || user.cards.length === 0) && (
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label="Пользователь не может получать выплаты без привязанной карты"
                      color="warning"
                      variant="outlined"
                    />
                  </Box>
                )}

                {isLoadingPayments ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : payments.length === 0 ? (
                  <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
                    Пока нет выплат
                  </Typography>
                ) : (
                  <List>
                    {payments.map((payment, index) => (
                      <React.Fragment key={payment.id}>
                        <ListItem>
                          <ListItemText
                                                         primary={
                               <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <Typography variant="h6">
                                 {formatAmountFromKopecks(payment.amount)}
                               </Typography>
                                                                   <Chip
                                    label={
                                      !payment.paymentId || payment.paymentId === 0 
                                        ? "Не удалось" 
                                        : payment.npdBill 
                                          ? "Выплачено" 
                                          : "В процессе"
                                    }
                                    color={
                                      !payment.paymentId || payment.paymentId === 0 
                                        ? "error" 
                                        : payment.npdBill 
                                          ? "success" 
                                          : "warning"
                                    }
                                    size="small"
                                  />
                               </Box>
                             }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Дата:</strong> {formatDate(payment.createdAt)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Назначение:</strong> {payment.purpose || 'Не указано'}
                                </Typography>
                                                                 {payment.npdBill && (
                                   <Button
                                     variant="text"
                                     size="small"
                                     startIcon={<Receipt />}
                                     onClick={() => window.open(payment.npdBill, '_blank')}
                                     sx={{ mt: 1 }}
                                   >
                                     Просмотреть чек НПД
                                   </Button>
                                 )}
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < payments.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose}>
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>

      <CreatePaymentDialog
        open={isCreatePaymentOpen}
        onClose={() => setIsCreatePaymentOpen(false)}
        users={[user]}
        onSuccess={() => {
          setIsCreatePaymentOpen(false);
          loadUserPayments(); // Перезагружаем выплаты
        }}
        selectedUserId={user.id}
      />


    </>
  );
};

export default UserDetailsDialog;
